import { ColDef, GridOptions, ServerSideStoreType } from 'ag-grid-community';

import TranslationHelper from '../../../../helpers/TranslationHelper';
import {
    IUser,
    TOptionalNormalizedData,
} from '../../../../state/app/collections/dataTypes';
import { IWorkflow, IWorkflowStatus } from '../../types';
import { gridValueGetter } from '../../../../components/SourceSetGrid/_utils';
import { TUserState } from '../../../../state/user';
import TimeFormatter from '@/app/helpers/TimeFormatter';

export const gridProps: GridOptions = {
    cacheBlockSize: 25,
    pagination: true,
    maxConcurrentDatasourceRequests: 2,
    paginationPageSize: 25,
    rowSelection: 'single',
};

export const serverSideGridProps = {
    ...gridProps,
    //aggrid type bug to be fixed in future versions - using enum instead of string result in broker partial data download feature
    serverSideStoreType: 'partial' as ServerSideStoreType,
};

export const generateDomainDataColumns = (
    rowData: any[],
    userSettings: TUserState
): ColDef[] => {
    if (!rowData || rowData.length === 0) return [];

    const domainKeys = Array.from(
        new Set(rowData.flatMap((row) => Object.keys(row.domainData || {})))
    );

    return domainKeys.map((key) => ({
        headerName: TranslationHelper.translate(`domainData.${key}`),
        field: `domainData.${key}`,
        valueGetter: (params) => {
            const value = params.data?.domainData?.[key] ?? '';
            if (TimeFormatter.isValidDate(value)) {
                return gridValueGetter({
                    value: value,
                    type: 'date',
                    userSettings: userSettings,
                });
            } else {
                return value;
            }
        },
        sortable: true,
        filter: true,
        resizable: true,
    }));
};

export const getColDefs = (
    users: TOptionalNormalizedData<IUser>,
    workflows: IWorkflow[],
    statuses: IWorkflowStatus[],
    userSettings: TUserState
): ColDef[] => {
    return [
        {
            field: 'status',
            headerName: TranslationHelper.translate('Status'),
            valueGetter: (params) => {
                const statusId = params.data.status;
                return statuses.find((status) => status.name === statusId)
                    ?.label;
            },
        },
        {
            field: 'name',
            headerName: TranslationHelper.translate('Name'),
        },
        {
            field: 'workflow',
            headerName: TranslationHelper.translate('Category'),
            valueGetter: (params) => {
                const workflowId = params.data.workflow;
                return workflows.filter((wf) => wf.name === workflowId)?.[0]
                    ?.label;
            },
        },
        {
            field: 'createdAt',
            headerName: TranslationHelper.translate('Created at'),
            valueGetter: (params) =>
                gridValueGetter({
                    value: params.data?.createdAt,
                    type: 'date',
                    userSettings: userSettings,
                }),
        },
        {
            field: 'updatedAt',
            headerName: TranslationHelper.translate('Updated at'),
            valueGetter: (params) =>
                gridValueGetter({
                    value: params.data?.updatedAt,
                    type: 'date',
                    userSettings: userSettings,
                }),
        },
        {
            field: 'assignedUser',
            headerName: TranslationHelper.translate('Assigned user'),
            valueGetter: (params) => {
                const userId = params.data.assignedUser as number;
                return users?.entities?.[userId]?.login || '';
            },
        },
        {
            field: 'notice.content',
            headerName: TranslationHelper.translate('Notice'),
        },
        {
            field: 'description',
            headerName: TranslationHelper.translate('Description'),
        },
    ];
};
